import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { Project } from '@hangar/shared';
import { useAppsOfProjects, useProjects } from '@/api/hooks';
import { stagger } from '@/lib/motion';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { EnvPill } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { NewProjectDialog } from '@/features/projects/NewProjectForm';
import { appsLabel, compactEnvironments } from '@/features/projects/projectsModel';

/** "New app": the project's page with Connect app open (`?connect=1`). */
function NewAppLink({ project: p }: { project: Project }) {
  return (
    <Button asChild size="sm" variant="ghost" className="shrink-0">
      <Link to="/projects/$slug" params={{ slug: p.slug }} search={{ connect: true }} data-testid={`project-new-app-${p.slug}`} aria-label={`New app in ${p.name}`}>
        <Icon name="add" size={16} />
        New app
      </Link>
    </Button>
  );
}

/** One project: name and slug, its environments and app count; the row opens its Apps & profiles page; "New app" connects one. */
function ProjectRow({ project: p, apps, index }: { project: Project; apps: number | undefined; index: number }) {
  return (
    <li className="enter flex min-w-0 items-center gap-1 pr-2 sm:pr-3" style={stagger(index)}>
      <Link to="/projects/$slug" params={{ slug: p.slug }} data-testid={`project-row-${p.slug}`} className="lift-row flex min-h-11 min-w-0 flex-1 items-center gap-3 px-4 py-2.5 sm:px-5">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate text-sm font-medium text-foreground">{p.name}</span>
          <span className="flex min-w-0 flex-wrap items-center gap-1">
            {compactEnvironments(p).map((e) => (
              <EnvPill key={e.id} env={e} />
            ))}
            <span className="font-mono text-xs text-subtle">· {appsLabel(apps)}</span>
          </span>
        </span>
        <Icon name="chevron_right" size={20} className="shrink-0 text-subtle" />
      </Link>
      <NewAppLink project={p} />
    </li>
  );
}

function ProjectsBody({ onCreate }: { onCreate: () => void }) {
  const projects = useProjects();
  const counts = useAppsOfProjects((projects.data ?? []).map((p) => p.id));
  if (projects.isLoading && !projects.data) return <Skeleton className="m-4 h-12 sm:m-5" />;
  if (projects.isError && !projects.data) return <ErrorState compact error={projects.error} onRetry={() => void projects.refetch()} />;
  if (!projects.data?.length) {
    return (
      <EmptyState
        testId="projects-empty"
        icon="account_tree"
        title="No projects yet"
        description="Create the first project: staging, preprod and production environments come with it."
        action={
          <Button variant="outline" onClick={onCreate}>
            <Icon name="add" size={18} />
            New project
          </Button>
        }
      />
    );
  }
  return (
    <ul className="flex min-w-0 flex-col divide-y" aria-label="Projects">
      {projects.data.map((p, i) => (
        <ProjectRow key={p.id} project={p} apps={counts[i]?.data?.length} index={i} />
      ))}
    </ul>
  );
}

/**
 * Overview · Projects: one compact list (name, environments, app count) that opens each project's Apps & profiles page,
 * plus "New project" (outline: Run build is the screen's one primary action). Apps and profiles themselves live on the
 * project page, so the Overview does not repeat them.
 */
export function ProjectsCompact() {
  const [creating, setCreating] = useState(false);
  return (
    <Card id="projects" className="enter min-w-0 scroll-mt-20" style={stagger(7)} data-testid="overview-projects">
      <CardHeader>
        <CardTitle>Projects</CardTitle>
        <CardDescription>Open a project to manage its apps and build profiles</CardDescription>
        <CardAction>
          <Button variant="outline" onClick={() => setCreating(true)} aria-haspopup="dialog">
            <Icon name="add" size={18} />
            New project
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="p-0 sm:p-0">
        <ProjectsBody onCreate={() => setCreating(true)} />
      </CardContent>
      <NewProjectDialog open={creating} onOpenChange={setCreating} />
    </Card>
  );
}
